import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

// Sidebar vars (sb*) are camelCase here, converted to --theme-sbBg → --theme-sbBg css var
// Sidebar.css reads: var(--theme-sbBg), var(--theme-sbText), etc.

export const themes = {
  // ── Warm Authority (default) ──────────────────────────────────────────
  enterprise: {
    name: 'Enterprise',
    category: 'Light',
    icon: '🏢',
    primary: '#4F46E5',
    primaryDark: '#3730A3',
    primaryLight: '#6366F1',
    accent: '#EEEAFF',
    background: '#F5F0E8',
    surface: '#FDF9F3',
    surfaceHover: '#F0EBE0',
    text: '#1C1A16',
    textSecondary: '#6B6560',
    border: '#E4DDD3',
    sidebar: '#2a4d8f',
    sidebarShadow: 'rgba(0, 0, 0, 0.22)',
    isDark: false,
    sbBg: '#2a4d8f',
    sbBorder: '#1e3a5f',
    sbBrandColor: '#F0EADC',
    sbTaglineColor: '#9FAFC6',
    sbText: '#D4E2F4',
    sbTextMuted: '#7A9EC8',
    sbHoverBg: 'rgba(255, 255, 255, 0.09)',
    sbActiveBg: 'rgba(255, 255, 255, 0.16)',
    sbActiveColor: '#FFFFFF',
    sbActiveIcon: '#93C5FD',
    sbActiveBar: '#60A5FA',
    sbFooterBg: '#1A3570',
    sbIconBtnBg: 'rgba(255, 255, 255, 0.12)',
    sbIconBtnColor: '#9FAFC6',
    sbScrollbar: 'rgba(255, 255, 255, 0.18)',
  },

  // ── Midnight Dark ─────────────────────────────────────────────────────
  microsoft: {
    name: 'Midnight Dark',
    category: 'Dark',
    icon: '🌑',
    primary: '#818cf8',
    primaryDark: '#6366f1',
    primaryLight: '#a5b4fc',
    accent: 'rgba(129, 140, 248, 0.12)',
    background: '#1f1f1f',
    surface: '#2d2d2d',
    surfaceHover: '#383838',
    text: '#e0e0e0',
    textSecondary: '#adadad',
    border: '#404040',
    sidebar: '#252525',
    sidebarShadow: 'rgba(0, 0, 0, 0.5)',
    isDark: true,
    sbBg: '#252525',
    sbBorder: '#333333',
    sbBrandColor: '#e0e0e0',
    sbTaglineColor: '#8a8a8a',
    sbText: '#c8c8c8',
    sbTextMuted: '#6e6e6e',
    sbHoverBg: 'rgba(255, 255, 255, 0.07)',
    sbActiveBg: 'rgba(129, 140, 248, 0.18)',
    sbActiveColor: '#e0e0e0',
    sbActiveIcon: '#A5B4FC',
    sbActiveBar: '#818CF8',
    sbFooterBg: '#1a1a1a',
    sbIconBtnBg: 'rgba(255, 255, 255, 0.08)',
    sbIconBtnColor: '#8a8a8a',
    sbScrollbar: 'rgba(255, 255, 255, 0.15)',
  },
};

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState('enterprise');

  useEffect(() => {
    const savedTheme = localStorage.getItem('app-theme');
    if (savedTheme && themes[savedTheme]) {
      setCurrentTheme(savedTheme);
    } else {
      setCurrentTheme('enterprise');
    }
  }, []);

  useEffect(() => {
    const theme = themes[currentTheme];

    if (!theme) {
      console.error(`Theme "${currentTheme}" not found. Falling back to "enterprise".`);
      setCurrentTheme('enterprise');
      return;
    }

    const root = document.documentElement;

    Object.entries(theme).forEach(([key, value]) => {
      if (key !== 'name') {
        root.style.setProperty(`--theme-${key}`, value);
      }
    });

    // Apply dark mode class for global dark overrides
    if (theme.isDark) {
      document.body.classList.add('theme-dark');
    } else {
      document.body.classList.remove('theme-dark');
    }

    localStorage.setItem('app-theme', currentTheme);
  }, [currentTheme]);

  const changeTheme = (themeName) => {
    if (themes[themeName]) {
      setCurrentTheme(themeName);
    }
  };

  return (
    <ThemeContext.Provider value={{ currentTheme, changeTheme, themes }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
